import type {
  Amenity,
  AmenityDetail,
  AmenityOrder,
  AmenityOrderDetails,
  CreateAmenityPayload,
  UpdateAmenityPayload,
  UploadAmenityImageResponse,
} from '../types/amenities.types';

import { toRelativeAmenityImageUrl } from './resolve-amenity-image-url';

function parseNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return fallback;
}

function parseBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') {
      return true;
    }
    if (normalized === 'false' || normalized === '0') {
      return false;
    }
  }

  return fallback;
}

function readString(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return '';
}

function readPhotoUrl(item: Record<string, unknown>): string | null {
  const photo =
    item.photoUrl ?? item.imageUrl ?? item.photo ?? item.image ?? null;

  if (typeof photo === 'string' && photo.trim() !== '') {
    return photo;
  }

  return null;
}

export function normalizeAmenity(raw: unknown): Amenity | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as Record<string, unknown>;
  const amenityCode = readString(item.amenityCode).trim();

  if (!amenityCode) {
    return null;
  }

  const amenityId = parseNumber(
    item.amenityId ?? item.id ?? item.amenity_id,
  );

  return {
    amenityId,
    amenityCode,
    name: readString(item.name),
    description: readString(item.description),
    price: parseNumber(item.price),
    branchCode: readString(item.branchCode),
    active: parseBoolean(item.active, true),
    delete: parseBoolean(item.delete, false),
    guestNumber: parseNumber(
      item.guestNumber ?? item.quantity ?? item.guest_number,
      1,
    ),
    formCode: readString(item.formCode) || null,
    photoUrl: readPhotoUrl(item),
  };
}

export function normalizeAmenitiesList(data: unknown): Amenity[] {
  if (!Array.isArray(data)) {
    return [];
  }

  return data
    .map(normalizeAmenity)
    .filter((amenity): amenity is Amenity => amenity !== null && !amenity.delete);
}

export function normalizeAmenityDetail(raw: unknown): AmenityDetail | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as Record<string, unknown>;
  const amenityCode = readString(item.amenityCode).trim();

  if (!amenityCode) {
    return null;
  }

  return {
    amenityCode,
    name: readString(item.name),
    description: readString(item.description),
    price: parseNumber(item.price),
    quantity: parseNumber(item.quantity, 1),
    photoUrl: readPhotoUrl(item),
  };
}

export function normalizeAmenityOrderDetails(raw: unknown): AmenityOrderDetails {
  if (!raw || typeof raw !== 'object') {
    return {
      bookingCode: '',
      paid: false,
      amenities: [],
    };
  }

  const item = raw as Record<string, unknown>;
  const amenitiesRaw = Array.isArray(item.amenities) ? item.amenities : [];

  return {
    bookingCode: readString(item.bookingCode),
    paid: parseBoolean(item.paid, false),
    payLink: readString(item.payLink) || null,
    amenities: amenitiesRaw
      .map(normalizeAmenityDetail)
      .filter((detail): detail is AmenityDetail => detail !== null),
  };
}

export function normalizeAmenityOrder(raw: unknown): AmenityOrder | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as Record<string, unknown>;
  const bookingCode = readString(item.bookingCode).trim();

  if (!bookingCode) {
    return null;
  }

  const cartRaw = item.cart;
  const cart: Record<string, number> = {};

  if (cartRaw && typeof cartRaw === 'object' && !Array.isArray(cartRaw)) {
    for (const [key, value] of Object.entries(cartRaw)) {
      cart[key] = parseNumber(value, 0);
    }
  }

  return {
    amenityOrderId: parseNumber(item.amenityOrderId) || undefined,
    bookingCode,
    paid: parseBoolean(item.paid, false),
    payLink: readString(item.payLink) || null,
    cart,
  };
}

export function normalizeUploadAmenityImageResponse(
  raw: unknown,
): UploadAmenityImageResponse {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid upload response');
  }

  const item = raw as Record<string, unknown>;

  return {
    id: parseNumber(item.id),
    fileName: readString(item.fileName),
    filePath: readString(item.filePath),
    fileType: readString(item.fileType),
    fileUrl: readString(item.fileUrl),
    storedDate: readString(item.storedDate),
  };
}

export function buildCreateAmenityBody(
  branchCode: string,
  fields: {
    name: string;
    description: string;
    price: number;
    guestNumber: number;
    active: boolean;
  },
): CreateAmenityPayload {
  return {
    branchCode,
    name: fields.name,
    description: fields.description,
    price: fields.price,
    guestNumber: fields.guestNumber,
    active: fields.active,
    delete: false,
    formCode: null,
    photoUrl: null,
  };
}

export function buildUpdateAmenityBody(
  amenity: Amenity,
  fields: {
    branchCode: string;
    name: string;
    description: string;
    price: number;
    guestNumber: number;
    active: boolean;
    photoUrl?: string | null;
  },
): UpdateAmenityPayload {
  const photoUrl =
    fields.photoUrl !== undefined
      ? toRelativeAmenityImageUrl(fields.photoUrl)
      : amenity.photoUrl ?? null;

  return {
    amenityId: amenity.amenityId,
    amenityCode: amenity.amenityCode,
    branchCode: fields.branchCode,
    name: fields.name,
    description: fields.description,
    price: fields.price,
    guestNumber: fields.guestNumber,
    active: fields.active,
    delete: amenity.delete ?? false,
    formCode: amenity.formCode ?? null,
    photoUrl,
  };
}

export function canUpdateAmenity(amenity: Amenity): boolean {
  return Boolean(amenity.amenityCode) && amenity.amenityId > 0;
}

export function isPersistedImageUrl(url: string | undefined | null): boolean {
  if (!url || url.trim() === '') {
    return false;
  }

  return !url.startsWith('blob:');
}
